// Simplified Payment System Types

// Payment method
export type PaymentMethod = 'cash' | 'transfer' | 'card' | 'other'

// Payment status
export type PaymentStatus = 'paid' | 'overdue' | 'not_set'

// Transaction type
export type TransactionType = 'payment' | 'charge' | 'adjust'

// Group Payment Config - базовая цена для группы
export interface GroupPaymentConfig {
  id: string
  group_id: string
  base_price: number // в копейках
  billing_day: number // 1-31
  currency: string
  description: string
  is_active: boolean
  created_at: string
  updated_at: string
  created_by: string
}

// Athlete Balance - текущий баланс спортсмена
export interface AthleteBalance {
  athlete_id: string
  group_id: string
  balance: number // в копейках (+ переплата, - долг)
  currency: string
  status: PaymentStatus
  last_charged_at?: string
  last_payment_at?: string
}

// Payment Transaction - история операций
export interface PaymentTransaction {
  id: string
  athlete_id: string
  group_id: string
  type: TransactionType
  amount: number // в копейках
  currency: string
  payment_method?: PaymentMethod
  description: string
  transaction_date: string
  created_at: string
  created_by: string
  deleted_at?: string
}

// Athlete Payment Override (индивидуальная цена / льгота)
export interface AthletePaymentOverride {
  id: string
  athlete_id: string
  group_id: string
  custom_price: number // в копейках
  reason: string
  currency: string
  is_active: boolean
  created_by: string
  created_at: string
  updated_at: string
}

// Request types

export interface CreateOverrideRequest {
  group_id: string
  custom_price: number // в рублях (конвертируется в копейки)
  reason: string
  currency?: string
}

export interface UpdateOverrideRequest {
  custom_price: number // в рублях
  reason: string
}

export interface CreateGroupPaymentConfigRequest {
  base_price: number // в рублях (конвертируется в копейки)
  billing_day: number
  currency?: string
  description?: string
}

export interface UpdateGroupPaymentConfigRequest {
  base_price: number // в рублях
  billing_day: number
  description?: string
}

export interface RecordPaymentRequest {
  group_id: string
  amount: number // в рублях (конвертируется в копейки)
  payment_method: PaymentMethod
  description?: string
  transaction_date?: string // ISO date (optional)
}

// Helper functions

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Наличные',
  transfer: 'Перевод',
  card: 'Карта',
  other: 'Другое',
}

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  paid: 'Оплачено',
  overdue: 'Долг',
  not_set: 'Не настроено',
}

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  paid: 'text-green-600 bg-green-100',
  overdue: 'text-red-600 bg-red-100',
  not_set: 'text-gray-500 bg-gray-100',
}

export const PAYMENT_STATUS_ICONS: Record<PaymentStatus, string> = {
  paid: '✅',
  overdue: '❌',
  not_set: '—',
}

// Конвертация рубли <-> копейки
export function rublesToKopecks(rubles: number): number {
  return Math.round(rubles * 100)
}

export function kopecksToRubles(kopecks: number): number {
  return kopecks / 100
}

// Форматирование суммы
export function formatAmount(kopecks: number, currency = 'RUB'): string {
  const rubles = kopecksToRubles(kopecks)
  return `${rubles.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency === 'RUB' ? '₽' : currency}`
}

// Форматирование баланса (с знаком)
export function formatBalance(kopecks: number, currency = 'RUB'): string {
  const rubles = kopecksToRubles(kopecks)
  const sign = rubles >= 0 ? '+' : ''
  return `${sign}${rubles.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency === 'RUB' ? '₽' : currency}`
}
